import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { LEARN } from "../../theme";
import { useBreakpoint } from "../../shell/LearnShell";
import { ConfettiBurst } from "../../feedback/Celebration";
import { seededShuffle, useCompleteOnce } from "./engineUtils";
import { Counter, IconSpeaker, ROOT_STYLE, RoundButton, SparkleBurst, StickerTile, choiceGrid, escapeRe, frost, tileMultForCount, useLetterAccent, useTileFeedback } from "./tapUtils";

const BLANK_RE = /(\bblank\b|_{2,})/i;

/**
 * Listen to a clue / sentence with a gap, tap the picture that completes it.
 * The word slides into the sentence when correct. Completes after every
 * prompt in the round is answered.
 */
export default function SentenceMeaningGame({ letter, config = {}, speak, replay, sfx, onComplete, onWrong, reduceMotion }) {
  const finish = useCompleteOnce(onComplete);
  const { accent, deep } = useLetterAccent(letter);
  const bp = useBreakpoint();
  const compact = bp === "xs" || bp === "sm";
  const bank = useMemo(() => {
    const m = new Map();
    (config.wordBank || config.items || []).forEach((it) => { if (it?.word) m.set(String(it.word).toLowerCase(), it); });
    return m;
  }, [config.wordBank, config.items]);

  const prompts = useMemo(
    () => seededShuffle(config.prompts || [], 52).slice(0, config.roundSize || config.prompts?.length || 4),
    [config.prompts, config.roundSize],
  );
  const [pi, setPi] = useState(0);
  const [solved, setSolved] = useState(false);
  const { shakes, pulses, shakeId, pulseId } = useTileFeedback();
  const prompt = prompts[pi];

  // Answer: an explicit bank word, else the bank word found inside the
  // completed sentence / spoken line (some configs only carry a catalog ref).
  const answer = useMemo(() => {
    if (!prompt) return null;
    const explicit = String(prompt.answer || prompt.answerWord || "").toLowerCase();
    if (explicit && bank.has(explicit)) return explicit;
    const texts = [prompt.completedSentence, prompt.spokenInstruction, prompt.prompt].filter(Boolean);
    let best = null;
    for (const text of texts) {
      for (const w of bank.keys()) {
        if (new RegExp(`(^|[^a-z])${escapeRe(w)}([^a-z]|$)`, "i").test(text) && (!best || w.length > best.length)) best = w;
      }
      if (best) break;
    }
    if (!best && explicit && (prompt.choices || []).some((c) => String(c).toLowerCase() === explicit)) return explicit;
    return best;
  }, [prompt, bank]);

  const choices = useMemo(() => {
    if (!prompt) return [];
    const listed = prompt.choices || [];
    if (listed.length) return listed.map((w) => bank.get(String(w).toLowerCase()) || { word: w });
    const all = [...bank.values()];
    const ans = answer ? bank.get(answer) : null;
    const others = seededShuffle(all.filter((x) => String(x.word).toLowerCase() !== answer), 70 + pi).slice(0, 3);
    return seededShuffle([ans, ...others].filter(Boolean), 80 + pi);
  }, [prompt, bank, answer, pi]);

  const spokenLine = prompt?.spokenInstruction || prompt?.prompt || prompt?.visibleFrame || config.prompt;

  // Each round's clue is the content of this game (the stage speaks the intro).
  useEffect(() => {
    if (prompt && spokenLine) speak?.(spokenLine);
  }, [pi, prompt, spokenLine, speak]);

  useEffect(() => {
    if (!solved) return undefined;
    const t = setTimeout(() => {
      if (pi + 1 >= prompts.length) { finish(); return; }
      setPi((x) => x + 1);
      setSolved(false);
    }, reduceMotion ? 350 : 1400);
    return () => clearTimeout(t);
  }, [solved, pi, prompts.length, finish, reduceMotion]);

  useEffect(() => { if (!prompts.length) finish(); }, [prompts.length, finish]);
  if (!prompt) return <div style={ROOT_STYLE} />;

  const tap = (it) => {
    if (solved) return;
    const key = String(it.word).toLowerCase();
    if (key !== answer) { shakeId(key); onWrong?.(); return; }
    sfx?.("correct");
    speak?.(prompt.completedSentence || it.word);
    pulseId(key);
    setSolved(true);
  };

  const frame = prompt.visibleFrame || prompt.prompt || prompt.spokenInstruction || "";
  const parts = frame.split(BLANK_RE);
  const hasGap = parts.length > 1;
  const answerWord = answer ? (bank.get(answer)?.word || answer) : "";
  const finalRound = solved && pi + 1 >= prompts.length;
  const mult = tileMultForCount(choices.length);

  return (
    <div style={ROOT_STYLE}>
      <ConfettiBurst active={finalRound} originY={40} count={36} reduceMotion={reduceMotion} />
      <div style={frost({ padding: "12px 14px 12px 18px", display: "flex", alignItems: "center", gap: 12, flexShrink: 0, margin: "0 auto 10px", width: "min(100%, 620px)", boxSizing: "border-box" })}>
        <AnimatePresence mode="wait" initial={false}>
          <motion.p
            key={pi}
            initial={reduceMotion ? false : { y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={reduceMotion ? undefined : { y: -10, opacity: 0 }}
            transition={{ duration: 0.2 }}
            style={{ margin: 0, flex: 1, minWidth: 0, fontSize: "clamp(19px, calc(var(--tile, 72px) * 0.3), 26px)", fontWeight: 700, lineHeight: 1.45, color: LEARN.color.ink }}
          >
            {hasGap
              ? parts.map((seg, i) => (BLANK_RE.test(seg) && i % 2 === 1 ? <Gap key={i} word={solved ? answerWord : ""} accent={accent} reduceMotion={reduceMotion} /> : <span key={i}>{seg}</span>))
              : (
                <>
                  <span>{frame}</span>
                  {solved && <Gap word={answerWord} accent={accent} reduceMotion={reduceMotion} />}
                </>
              )}
          </motion.p>
        </AnimatePresence>
        <RoundButton ariaLabel="Hear the clue again" onClick={() => (replay || speak)?.(spokenLine)} accent={accent} deep={deep} reduceMotion={reduceMotion} size={44}>
          <IconSpeaker size={20} />
        </RoundButton>
        <Counter done={pi + (solved ? 1 : 0)} total={prompts.length} accent={accent} />
      </div>

      <div style={{ flex: 1, minHeight: 0, overflow: "auto", display: "flex", alignItems: "center", justifyContent: "center", padding: 4 }}>
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={pi}
            initial={reduceMotion ? false : { opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={reduceMotion ? undefined : { opacity: 0, scale: 0.96 }}
            transition={{ duration: 0.2 }}
            style={choiceGrid(choices.length, compact)}
          >
            {choices.map((it, i) => {
              const key = String(it.word).toLowerCase();
              const isAnswer = key === answer;
              return (
                <div key={key} style={{ position: "relative" }}>
                  <StickerTile
                    letter={letter}
                    assetName={it.assetName}
                    word={it.word}
                    label={it.word}
                    size={mult}
                    index={i}
                    accent={accent}
                    reduceMotion={reduceMotion}
                    shake={shakes[key] || 0}
                    pulse={pulses[key] || 0}
                    state={solved ? (isAnswer ? "correct" : "dim") : "idle"}
                    disabled={solved}
                    onTap={() => tap(it)}
                  />
                  {solved && isAnswer && <SparkleBurst trigger={pulses[key] || 0} color={accent} reduceMotion={reduceMotion} distance={70} />}
                </div>
              );
            })}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

function Gap({ word, accent, reduceMotion }) {
  return (
    <motion.span
      layout={!reduceMotion}
      style={{
        display: "inline-block", verticalAlign: "baseline", minWidth: "2.6em", margin: "0 0.15em", padding: "0 0.45em",
        borderRadius: 10, lineHeight: 1.3,
        background: word ? accent : "rgba(11,26,51,0.05)", color: word ? "#fff" : "transparent",
        border: word ? `2px solid ${accent}` : `2px dashed ${accent}88`, textAlign: "center",
        transition: reduceMotion ? "none" : "background 220ms, border-color 220ms",
      }}
    >
      {word ? (
        <motion.span initial={reduceMotion ? false : { scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 420, damping: 22 }} style={{ display: "inline-block" }}>
          {word}
        </motion.span>
      ) : " "}
    </motion.span>
  );
}
